/**
 * 첨부파일 API — backend /attachments/* 와 1:1.
 * 영업 + 비상벨 공용 PDF 첨부.
 */

import type { ApiResponse } from './types';
import { openPdfPreview } from './pdf-preview';

const BASE = '/api';

export type AttachmentParentKind = 'sales' | 'bell' | 'po' | 'partner' | 'maintenance' | 'devplan' | 'devreq' | 'purchase';

/** 개발 계획 글에 붙는 첨부의 종류 — 서버 `DEVPLAN_ATTACH_KIND` 와 같은 글자여야 한다 (v2.180.0) */
export const DEVPLAN_ATTACH_KIND = '첨부';

/** 매입에 붙는 증빙(거래명세서·주문내역 캡처)의 종류 — 서버 `PURCHASE_ATTACH_KIND` 와 같은 글자 (v2.308.0) */
export const PURCHASE_ATTACH_KIND = '매입증빙';

/**
 * 유지보수 계약에 붙일 수 있는 문서 종류 3가지 (v2.105.0 · 사용자 확정 2026-08-28).
 * 서버 `MAINTENANCE_ATTACH_KINDS` 와 같은 목록이어야 한다 —
 * 어긋나면 화면에서 고른 종류를 서버가 400 으로 되돌린다.
 */
export const MAINTENANCE_ATTACH_KINDS = ['견적서', '계약서', '기타서류'] as const;
export type MaintenanceAttachKind = (typeof MAINTENANCE_ATTACH_KINDS)[number];

export interface AttachmentMeta {
  id: number;
  parent_kind: AttachmentParentKind;
  parent_id: string;
  kind: string;
  filename: string;
  mime_type: string;
  file_size: number;
  uploaded_by: string | null;
  uploaded_at: string;
}

export async function listAttachments(
  parentKind: AttachmentParentKind,
  parentId: string,
  kind?: string,
): Promise<AttachmentMeta[]> {
  const qs = new URLSearchParams({ parentKind, parentId });
  if (kind) qs.set('kind', kind);
  const res = await fetch(`${BASE}/attachments?${qs}`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<{ attachments: AttachmentMeta[] }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.attachments;
}

/** 문서관리 — 영업건 첨부(kind) 전체 + 영업건 정보 (고객발주서·분할납품요구서 탭) */
export interface SalesDocRow {
  id: number;
  sales_id: string;
  kind: string;
  filename: string;
  mime_type: string;
  file_size: number;
  uploaded_by: string | null;
  uploaded_at: string;
  end_user: string | null;
  project_name: string | null;
  status: string;
  emp_name: string | null;
  invoice_date: string | null;
  company_id: string | null;
  proposal_company: string | null;
}

export async function listAttachmentsByKind(kind: string): Promise<SalesDocRow[]> {
  const res = await fetch(`${BASE}/attachments/by-kind?kind=${encodeURIComponent(kind)}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<{ documents: SalesDocRow[] }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.documents;
}

/**
 * 업로드와 함께 이뤄지는 NAS 보관 결과 (v2.96.0).
 *   attempted=false → 이 문서는 NAS 보관 대상이 아니다(구입견적서 등). 조용히 넘어간 것.
 *   attempted=true & saved=false → 못 했다. reason 이 그 이유.
 */
export interface NasAutoSaveResult {
  attempted: boolean;
  saved: boolean;
  path: string | null;
  reason: string | null;
}

/**
 * NAS 보관 실패를 알린다 — **여기 한 곳에서만** 한다 (v2.96.0).
 *
 * ⭐ 화면마다 따로 두면 어느 한 곳에서 빠뜨렸을 때 **조용히 안 올라간다.**
 *   업로드 통로가 8곳이라 그런 구멍이 생기기 쉽다.
 * ⭐ 여러 파일을 한꺼번에 올릴 때(영업건 신규 저장 등) 경고가 쏟아지지 않게 3초 안에는 한 번만.
 */
let lastNasAlertAt = 0;
function warnNasFailure(reason: string): void {
  const now = Date.now();
  if (now - lastNasAlertAt < 3000) return;
  lastNasAlertAt = now;
  window.alert(
    `파일은 포털에 저장됐습니다. 다만 NAS 보관은 하지 못했습니다.\n\n${reason}\n\n` +
      '문서 옆 💾 단추로 다시 시도할 수 있습니다.',
  );
}

/**
 * 고객 발주서를 올렸더니 서버가 영업건의 계약일·진행단계를 바꾼 내용 (v2.204.0 · 서버 lib/sales/po-arrival.ts).
 * 바꾼 것이 없으면 응답에 null 로 온다.
 */
export interface PoArrivalResult {
  /** 이번에 채운 계약일(YYYY-MM-DD) — 이미 있어서 안 채웠으면 null */
  contractDateFilled: string | null;
  /** 단계를 바꿨으면 바꾸기 전 단계 · 안 바꿨으면 null */
  stageFrom: string | null;
  /** 단계를 바꿨으면 '발주' · 안 바꿨으면 null */
  stageTo: string | null;
}

export async function uploadAttachment(
  parentKind: AttachmentParentKind,
  parentId: string,
  kind: string,
  file: File,
  options?: { replace?: boolean },
): Promise<{
  id: number; filename: string; mime_type: string; file_size: number;
  replaced?: boolean; nas?: NasAutoSaveResult;
  /** 영업건 고객 발주서일 때만 — 서버가 계약일·단계를 바꿨으면 그 내용 */
  salesAuto?: PoArrivalResult | null;
  /** 영업건 세금계산서 PDF 일 때만 (v2.311.0 · #25) — 발행요청을 닫았나·수신 기록을 이었나 · notice = 화면에 알릴 한 줄(없으면 null) */
  taxInvoice?: { kind: string; notice: string | null } | null;
}> {
  const fd = new FormData();
  fd.append('parentKind', parentKind);
  fd.append('parentId', parentId);
  fd.append('kind', kind);
  fd.append('file', file);
  if (options?.replace) fd.append('replace', 'true');
  const res = await fetch(`${BASE}/attachments`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  const json = (await res.json()) as ApiResponse<{
    id: number; filename: string; mime_type: string; file_size: number;
    replaced?: boolean; nas?: NasAutoSaveResult; salesAuto?: PoArrivalResult | null;
    taxInvoice?: { kind: string; notice: string | null } | null;
  }>;
  if (!json.ok) throw new Error(json.error.message);
  const nas = json.data.nas;
  if (nas?.attempted && !nas.saved && nas.reason) warnNasFailure(nas.reason);
  return json.data;
}

/** 지운 결과 — 영업건 세금계산서면 되돌린 수신 기록 알림(v2.311.0 · 없으면 null) */
export async function deleteAttachment(id: number): Promise<{ id: number; taxInvoice?: { notice: string } | null }> {
  const res = await fetch(`${BASE}/attachments/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<{ id: number; taxInvoice?: { notice: string } | null }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 인라인 렌더 URL (서버가 Content-Disposition: inline 으로 응답 → iframe/새 탭에서 표시) */
export function attachmentInlineUrl(id: number): string {
  return `${BASE}/attachments/${id}/download?inline=1`;
}

/** 새 탭에서 PDF 보기 (브라우저가 인라인 렌더 + 다운로드 옵션 제공) */
export function openAttachmentInNewTab(id: number): void {
  window.open(attachmentInlineUrl(id), '_blank', 'noopener');
}

/**
 * 메일로 내보낼 일이 없는 첨부 종류 — **두 자리가 같이 쓴다**(어느 화면에서 열든 같아야 하므로 여기 한 곳):
 *   ① 메일 창 [+ 포털 보관 문서] 목록에서 뺀다
 *   ② 첨부 미리보기에 ✉ 를 띄우지 않는다(목록에서만 빼면 ✉ 로 여전히 붙는다)
 *
 * - 「발주서」= **고객이 우리에게 준 발주서**다 (우리 매입 발주서가 아니다 —
 *   lib 주석·SalesDocsTab noun 참조 · 매입 발주서 메일은 docType 'po' 라 이 목록과 무관).
 *   받은 문서라 우리가 메일로 보낼 일이 없다 — v2.112.1(09-01) 미리보기 ✉ 제외 →
 *   v2.233.2(10-08) 메일 창 목록에서도 제외(사용자 「고객발주서도 빼자」).
 * - 「판매보고서」 — v2.233.1(10-08) 사용자 「판매보고서를 메일로 첨부할 일은 없어」.
 *   영업건 첨부 중 가장 많은 종류(운영 115건)라 목록을 가장 많이 차지하고 있었다.
 * 운영 발송 기록(09-03~10-08 · 22통)에서 둘 다 메일로 나간 적 0번.
 */
export const MAIL_PICKER_HIDDEN_KINDS: readonly string[] = ['발주서', '판매보고서'];
const NO_MAIL_ATTACHMENT_KINDS = MAIL_PICKER_HIDDEN_KINDS;

/** 첨부 파일을 팝업 미리보기로 표시 (다운로드 대신 내용 표시 — 팝업 안에 저장 버튼 있음)
 *  v2.108.0: 미리보기 헤더의 ✉ 버튼으로 이 첨부를 메일에 실을 수 있다 (보관 문서 첨부 통로).
 *  ⚠kind 를 넘기지 않으면 ✉ 가 붙는다 — 목록에서 부를 때는 첨부 객체를 통째로 넘길 것. */
export function previewAttachment(meta: {
  id: number; filename: string; mime_type?: string; file_size?: number; kind?: string;
}): void {
  const noMail = meta.kind != null && NO_MAIL_ATTACHMENT_KINDS.includes(meta.kind);
  openPdfPreview({
    url: attachmentInlineUrl(meta.id),
    downloadUrl: `${BASE}/attachments/${meta.id}/download`,
    filename: meta.filename,
    mime: meta.mime_type,
    ...(noMail ? {} : {
      mailDoc: {
        docRefs: [{
          docType: 'attachment',
          docId: meta.id,
          label: meta.filename,
          ...(meta.file_size != null ? { sizeHint: meta.file_size } : {}),
        }],
        subject: meta.filename.replace(/\.[^.]+$/, ''),
      },
    }),
  });
}

/** 강제 다운로드 (anchor + download attr) */
export function downloadAttachment(id: number, filename: string): void {
  const a = document.createElement('a');
  a.href = `${BASE}/attachments/${id}/download`;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
